import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFire, faShareNodes } from '@fortawesome/free-solid-svg-icons';
import Modal from '../../../components/Modal';
import EarnCommission from '../Shopee/EarnCommission';

/**
 * Kết quả tạo link đặt món ShopeeFood.
 *
 * ShopeeFood không có API sản phẩm nên không hiển thị được ảnh/giá/hoa hồng như
 * Shopee/Lazada — card này giữ đúng bố cục "kết quả tìm kiếm" của các sàn khác
 * (header gradient + card trắng + nút hành động) nhưng nội dung là link đặt món.
 *
 * Link tracking KHÔNG in ra màn (tránh user sửa/copy nhầm link cụt): nút "Đặt
 * món ngay" mở thẳng link gốc, còn copy/chia sẻ nằm trong modal thưởng thêm
 * (dùng lại `EarnCommission` của Shopee) với link rút gọn Sansale.
 */

// Cơ chế thưởng thêm — bản ShopeeFood (đặt món, không phải mua sản phẩm)
const SHOPEEFOOD_MECHANISM_ITEMS = [
    'Chia sẻ link đặt món này cho bạn bè, người thân. Khi họ đặt món từ link của bạn, bạn sẽ nhận được hoàn tiền thưởng.',
    'Link sau khi copy và gửi đi vẫn dùng được lâu dài, nhưng nên thay link 3 – 6 tháng/lần vì quán/món trên ShopeeFood có thể đổi hoặc ngừng bán.',
    'Điều kiện và điều khoản hoàn tiền áp dụng tương tự như khi bạn tự đặt món.',
    'Quán/món không nằm trong chương trình hoàn tiền thì đơn sẽ không được ghi nhận.',
];

function ShopeeFoodLinkResult({ link, shortLink, isLoading, errorMsg }) {
    const [openShare, setOpenShare] = useState(false);

    return (
        <div>
            <div className="flex gap-1 justify-start items-center p-2">
                <div className="h-10 aspect-square bg-gradient-to-br from-[#ff7a00] to-[#ffb020] p-2 rounded-full">
                    <FontAwesomeIcon
                        icon={faFire}
                        className="w-full h-full text-white"
                    />
                </div>
                <div className="flex flex-col text-sm text-content-2">
                    <span className="font-bold">LINK ĐẶT MÓN HOÀN TIỀN</span>
                </div>
            </div>

            <div className="px-2">
                {isLoading && (
                    <div className="flex gap-3 w-full bg-surface rounded-lg p-2">
                        <div className="w-1/3 aspect-square rounded-lg bg-surface-raised animate-hightLight" />
                        <div className="flex-1 flex flex-col justify-between gap-2 py-1">
                            <div className="w-full h-4 bg-surface-raised rounded animate-hightLight" />
                            <div className="w-2/3 h-4 bg-surface-raised rounded animate-hightLight" />
                            <div className="w-full h-10 bg-surface-raised rounded animate-hightLight" />
                        </div>
                    </div>
                )}

                {!isLoading && !!errorMsg && (
                    <div className="bg-surface rounded-lg p-4 text-center text-sm text-red-600">
                        {errorMsg}
                    </div>
                )}

                {!isLoading && !errorMsg && !!link && (
                    <div className="bg-surface rounded-lg p-2 flex flex-col gap-2">
                        <div className="flex gap-3">
                            <div className="w-1/3 aspect-square rounded-lg overflow-hidden bg-gradient-to-br from-[#ff7a00] to-[#ffb020] flex items-center justify-center p-2">
                                <div className="w-full h-full bg-surface rounded-md flex items-center justify-center p-2">
                                    <img
                                        src="img/ui/shopeefood_full.png"
                                        alt="shopeefood"
                                        className="w-full object-contain"
                                    />
                                </div>
                            </div>
                            <div className="flex-1 flex flex-col justify-between gap-2">
                                <div className="flex flex-col gap-1">
                                    <span className="font-semibold text-sm">
                                        Link đặt món của bạn đã sẵn sàng
                                    </span>
                                    <span className="text-[11px] text-content-3">
                                        Bấm <strong>Đặt món ngay</strong> để mở
                                        ShopeeFood, hoặc chia sẻ link cho bạn bè
                                        cùng đặt để nhận thưởng.
                                    </span>
                                </div>
                                <a
                                    href={link}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="p-2 md:p-3 rounded w-full font-bold text-center text-white bg-gradient-to-r from-[#ff7a00] to-[#ffb020]"
                                >
                                    ĐẶT MÓN NGAY
                                </a>
                            </div>
                        </div>

                        {/* Đặt qua mục ShopeeFood trong app Shopee bị mất tracking → nhắc thật rõ */}
                        <div className="text-xs text-amber-800 bg-amber-50 border border-amber-300 rounded p-2 flex flex-col gap-1">
                            <p className="font-bold">
                                ⚠️ Chỉ hoàn tiền khi đặt trong app ShopeeFood
                            </p>
                            <p>
                                Đặt qua mục ShopeeFood trong app <strong>Shopee</strong>{' '}
                                sẽ <strong>KHÔNG</strong> được ghi nhận. Chưa có app
                                ShopeeFood thì tải về trước, rồi quay lại bấm{' '}
                                <strong>Đặt món ngay</strong>.
                            </p>
                        </div>

                        <button
                            type="button"
                            onClick={() => setOpenShare(true)}
                            className="p-2 rounded bg-gray-500 text-white font-semibold text-sm flex items-center justify-center gap-2"
                        >
                            CHIA SẺ NHẬN THƯỞNG
                            <FontAwesomeIcon icon={faShareNodes} />
                        </button>

                        <div className="text-[11px] text-content-3 bg-orange-50 border border-orange-100 rounded p-2 flex flex-col gap-1">
                            <p>
                                Đặt món trong app ShopeeFood như bình thường. Nếu quán/món có
                                hoàn tiền, đơn sẽ được ghi nhận sau 24–48h và
                                hiện ở mục <strong>Đơn hàng của tôi</strong>.
                            </p>
                            <p>
                                Trường hợp quán/món không có hoàn tiền thì đơn
                                sẽ không được ghi nhận.
                            </p>
                        </div>
                    </div>
                )}
            </div>

            <Modal isOpen={openShare}>
                <EarnCommission
                    setOpenModal={setOpenShare}
                    platform="shopeefood"
                    presetLink={shortLink || link}
                    mechanismItems={SHOPEEFOOD_MECHANISM_ITEMS}
                />
            </Modal>
        </div>
    );
}

export default ShopeeFoodLinkResult;
